import type { ParsedUrlQuery } from 'querystring';

// Anchors and non-navigational schemes aren't page navigations, so params must
// not be appended to them. Everything else (relative or external) forwards.
const NON_FORWARDABLE_HREF = /^(#|tel:|mailto:|sms:|javascript:)/i;

/**
 * Append the current page's query params to a destination URL so tracking
 * survives the navigation, including onto external links. Returns the href
 * unchanged for anchors, non-navigational schemes, or when there's nothing to
 * forward. Drops Next.js's `page` route param, keeps repeated params as repeated
 * keys, and inserts params before any `#fragment`.
 */
export function buildUrlWithParams(baseUrl: string, query: ParsedUrlQuery): string {
	if (!baseUrl || NON_FORWARDABLE_HREF.test(baseUrl)) return baseUrl;

	const { page: _page, ...forwarded } = query;
	// Append array values as repeated keys (e.g. utm_content=a&utm_content=b)
	// rather than letting URLSearchParams comma-join them into one value.
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(forwarded)) {
		if (Array.isArray(value)) {
			value.forEach((entry) => params.append(key, entry));
		} else if (value !== undefined) {
			params.append(key, value);
		}
	}

	const queryString = params.toString();
	if (!queryString) return baseUrl;

	// Insert params before any #fragment so they land in the query string rather
	// than inside the fragment (which the browser never sends to the server).
	const hashIndex = baseUrl.indexOf('#');
	const path = hashIndex === -1 ? baseUrl : baseUrl.slice(0, hashIndex);
	const fragment = hashIndex === -1 ? '' : baseUrl.slice(hashIndex);

	const separator = path.includes('?') ? '&' : '?';
	return `${path}${separator}${queryString}${fragment}`;
}
